"use client";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useMatches } from "@/hooks/useMatches";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import {
  splitCorrectScoresListToSections,
  formatCorrectScoresForGrid,
} from "@/utils/matchInfo";
import { LIVE_STATUSES } from "@/contants";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { formatDateTime } from "@/utils/format";
import providerKSport from "@/assets/images/demo-2/provider-k-sport.webp";
import USOtherBetsDropdown from "../USOtherBetsDropdown/USOtherBetsDropdown";
import ViuGoTipsAiModal from "../ViuGoTipsAiModal";
import viugoTipsAiSparkle from "@/assets/images/viugo/kbv-v16-tips-ai-sparkle.svg";
import USMatchMobile from "./USMatchMobile";
import USMatchDesktop from "./USMatchDesktop";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function USMatch({ match, leagueName, index }) {
  const [showOther, setShowOther] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const [openTipsAiModal, setOpenTipsAiModal] = useState(false);
  const [tipsAiInsightData, setTipsAiInsightData] = useState(null);
  const [loadingTipsAiInsight, setLoadingTipsAiInsight] = useState(false);
  const { getMatchOdds, getMatchInsight } = useMatches();

  const { getOdds, getScore } = useLiveDataForMatch(match?.id);
  const liveOdds = getOdds();
  const liveScore = getScore();

  const statusNum =
    liveScore?.status !== undefined ? Number(liveScore.status) : Number(match.status);

  const {
    currentHomeScore,
    currentAwayScore,
    homeYellow,
    homeRed,
    awayYellow,
    awayRed,
    currentHomeCorner,
    currentAwayCorner,
  } = useMatchData(liveScore, match);
  const isLive = LIVE_STATUSES.includes(statusNum);

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const odds = useMatchOdds(match, filterOdds, companyIdOdds);
  const {
    handicapValue,
    homeHandicapOdds,
    awayHandicapOdds,
    homeHandicapTrend,
    awayHandicapTrend,
    handicapHalfValue,
    homeHandicapHalfOdds,
    awayHandicapHalfOdds,
    homeHandicapHalfTrend,
    awayHandicapHalfTrend,
    overUnderValue,
    overOdds,
    underOdds,
    overTrend,
    underTrend,
    overUnderHalfValue,
    overHalfOdds,
    underHalfOdds,
    overHalfTrend,
    underHalfTrend,
    homeEuropeOdds,
    awayEuropeOdds,
    instantDrawOdds,
    homeEuropeTrend,
    awayEuropeTrend,
    drawTrend,
    homeEuropeHalfOdds,
    awayEuropeHalfOdds,
    drawHalfOdds,
    homeEuropeHalfTrend,
    awayEuropeHalfTrend,
    drawHalfTrend,
  } = odds;

  const homeLogo = match?.homeTeam?.logo || match?.homeLogo;
  const awayLogo = match?.awayTeam?.logo || match?.awayLogo;

  const { date: matchDate, time: matchTimeText } = formatDateTime(match?.matchTime);

  const correctScoresListFull = useMemo(
    () =>
      otherOddsData?.correctScoresOdds
        ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds)
        : [],
    [otherOddsData],
  );
  const correctScoresFull = useMemo(
    () => splitCorrectScoresListToSections(correctScoresListFull),
    [correctScoresListFull],
  );
  const totalCornersOdds = useMemo(
    () =>
      Array.isArray(otherOddsData?.totalCornersOdds)
        ? otherOddsData.totalCornersOdds
        : [],
    [otherOddsData],
  );
  const hasOtherData = useMemo(
    () =>
      (otherOddsData?.goalOddEvenOdds?.length > 0) ||
      correctScoresListFull.length > 0 ||
      totalCornersOdds.length > 0,
    [otherOddsData, correctScoresListFull.length, totalCornersOdds.length],
  );

  const otherBetsCount = 3;

  const handleOtherClick = (e) => {
    e.stopPropagation();
    setShowOther((prev) => !prev);
    if (!showOther && !otherOddsData && match?.id) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) setOtherOddsData(data.data);
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const OtherBetsButton = ({ className = "" }) => (
    <div
      role="button"
      tabIndex={0}
      onClick={handleOtherClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOtherClick(e);
        }
      }}
      className={className}
    >
      <img
        className="h-[18px] w-[18px] object-contain"
        src={providerKSport}
        alt=""
        aria-hidden="true"
      />
      <span>
        Kèo khác +{otherBetsCount}
      </span>
    </div>
  );

  const handleOpenTipsAiModal = async () => {
    setOpenTipsAiModal(true);
    if (tipsAiInsightData || loadingTipsAiInsight) return;

    setLoadingTipsAiInsight(true);
    try {
      const insightResponse = await getMatchInsight(match?.id);
      if (insightResponse?.success && insightResponse?.data) {
        setTipsAiInsightData(insightResponse.data);
      }
    } finally {
      setLoadingTipsAiInsight(false);
    }
  };

  const TipsAiButton = ({ className = "" }) => (
    <button
      type="button"
      onClick={handleOpenTipsAiModal}
      className={`kbv-tips-btn-indigo-slate relative flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap border border-solid font-semibold text-white transition hover:-translate-y-[1px] ${className}`}
    >
      <img
        src={viugoTipsAiSparkle}
        alt=""
        width={16}
        height={16}
        className="h-[18px] w-[18px] shrink-0"
        aria-hidden="true"
      />
      <div>Tips AI</div>
    </button>
  );

  const commonProps = {
    isLive,
    match,
    statusNum,
    liveScore,
    matchTimeText,
    matchDate,
    homeLogo,
    awayLogo,
    handicapValue,
    homeHandicapOdds,
    homeHandicapTrend,
    awayHandicapOdds,
    awayHandicapTrend,
    overUnderValue,
    overOdds,
    overTrend,
    underOdds,
    underTrend,
    homeEuropeOdds,
    homeEuropeTrend,
    awayEuropeOdds,
    awayEuropeTrend,
    instantDrawOdds,
    drawTrend,
    homeEuropeHalfOdds,
    homeEuropeHalfTrend,
    drawHalfOdds,
    drawHalfTrend,
    awayEuropeHalfOdds,
    awayEuropeHalfTrend,
    TipsAiButton,
    OtherBetsButton,
  };

  return (
    <>
      <USMatchMobile 
        leagueName={leagueName} 
        currentHomeScore={currentHomeScore}
        currentAwayScore={currentAwayScore}
        {...commonProps} 
      />

      <USMatchDesktop 
        handicapHalfValue={handicapHalfValue}
        homeHandicapHalfOdds={homeHandicapHalfOdds}
        homeHandicapHalfTrend={homeHandicapHalfTrend}
        awayHandicapHalfOdds={awayHandicapHalfOdds}
        awayHandicapHalfTrend={awayHandicapHalfTrend}
        overUnderHalfValue={overUnderHalfValue}
        overHalfOdds={overHalfOdds}
        overHalfTrend={overHalfTrend}
        underHalfOdds={underHalfOdds}
        underHalfTrend={underHalfTrend}
        currentHomeScore={currentHomeScore}
        currentAwayScore={currentAwayScore}
        {...commonProps} 
      />

      {showOther && (
        <USOtherBetsDropdown
          loadingOtherOdds={loadingOtherOdds}
          otherOddsData={otherOddsData}
          hasOtherData={hasOtherData}
          correctScoresListFull={correctScoresListFull}
          correctScoresFull={correctScoresFull}
          totalCornersOdds={totalCornersOdds}
          odds={{
            ...filterOdds,
            homeEuropeOdds,
            awayEuropeOdds,
            instantDrawOdds,
            homeEuropeTrend,
            awayEuropeTrend,
            drawTrend,
            homeHandicapHalfOdds,
            awayHandicapHalfOdds,
            homeHandicapHalfTrend,
            awayHandicapHalfTrend,
          }}
          showOther={showOther}
        />
      )}

      <ViuGoTipsAiModal
        open={openTipsAiModal}
        onClose={() => setOpenTipsAiModal(false)}
        match={match}
        matchInfo={{
          formattedDate: matchDate,
          formattedGio: matchTimeText,
          isLive,
        }}
        statsInfo={{
          currentHomeScore,
          currentAwayScore,
          homeYellow,
          homeRed,
          awayYellow,
          awayRed,
          currentHomeCorner,
          currentAwayCorner,
        }}
        oddsInfo={{
          handicapValue,
          homeHandicapOdds,
          awayHandicapOdds,
          overUnderValue,
          overOdds,
          underOdds,
          homeEuropeOdds,
          awayEuropeOdds,
          instantDrawOdds,
          handicapHalfValue,
          homeHandicapHalfOdds,
          awayHandicapHalfOdds,
        }}
        leagueName={leagueName}
        leagueLogo={match?.league?.logo || match?.leagueLogo || ""}
        homeName={match.homeName}
        homeLogo={homeLogo}
        awayName={match.awayName}
        awayLogo={awayLogo}
        matchDate={matchDate}
        matchTime={matchTimeText}
        matchTimestamp={match?.matchTime}
        headerScore={`${currentHomeScore ?? 0} - ${currentAwayScore ?? 0}`}
        liveScoreToday={liveScore}
        matchStatusNum={statusNum}
        matchTimeUnix={match?.matchTime}
        halfStartTime={match?.halfStartTime}
        isMatchLive={isLive}
        statusLabel={isLive ? "Trực tiếp" : "Sắp diễn ra"}
        insightData={tipsAiInsightData}
        insightLoading={loadingTipsAiInsight}
      />
    </>
  );
}

